/**
 * SectionRenderer — 섹션 데이터 디스패처.
 *
 * 백엔드가 주는 section({kind,key,title,body,image_url}) 과 property 를 받아
 * 알맞은 섹션 컴포넌트로 렌더한다. 패널/상세/바텀시트가 sections.map(SectionRenderer)
 * 로 동일하게 렌더하기 위한 단일 진입점.
 *
 * 정책:
 *   - 순수 컴포넌트(fetch 없음). property 는 props 로 주입.
 *   - kind==='custom' → CustomProse (+ 이미지)
 *   - kind==='auto'   → key 스위치로 adapter 결과를 컴포넌트에 연결
 *   - 렌더할 데이터가 없는 섹션은 null 을 반환한다(부모가 자연히 skip).
 */

import { pyeongForType } from '../../../domain/property/pyeong';
import { canContactProperty } from '../../../domain/inquiry/contactPolicy';
import React from 'react';
import { Property } from '../../../services/api';
import {
  buildPriceRows,
  buildBasicRows,
  buildPlanCells,
  buildBenefitView,
  buildFloorPlanImages,
  summaryData,
  todayLocalISO,
} from '../../../domain/property/sectionAdapters';
import {
  buildTypePriceRows,
  buildScheduleSteps,
  buildPriceCaption,
  getPriceBasis,
  priceBasisLabel,
} from '../../../domain/property/detailV2';
import { TypePriceSection } from './TypePriceSection';
import { ScheduleTimeline } from './ScheduleTimeline';
import { FloorplanTabs } from './FloorplanTabs';
import { InfoIcon, PlanIcon, CalendarIcon, TableIcon } from './SectionIcons';
import { formatSubscriptionDate } from '../../../utils/formatters';
import { SectionCard } from './SectionCard';
import { CustomProse } from './CustomProse';
import { PriceTable } from './PriceTable';
import { DataRow } from './DataRow';
import { PlanGrid } from './PlanGrid';
import { PropertyStatusChip } from './PropertyStatusChip';
import { BenefitSection } from './BenefitSection';
import { resolveContactPhone } from '../../../domain/inquiry/resolveContactPhone';

export interface SectionDescriptor {
  kind: string;
  key: string;
  title?: string;
  body?: string | null;
  image_url?: string | null;
}

export interface SectionRendererProps {
  section: SectionDescriptor;
  property: Property;
  /** 'detail' = 상세 본문(넓은 컬럼) · 'panel' = 지도 패널 등 좁은 컨테이너(기본). 혜택안내 카드 열 수 분기용 (v2i) */
  variant?: 'detail' | 'panel';
}

/** 커스텀 섹션 — 관리자 작성 본문(+이미지). */
function renderCustom(section: SectionDescriptor): React.ReactElement | null {
  const hasBody = !!(section.body && section.body.trim());
  const hasImage = !!(section.image_url && section.image_url.trim());
  if (!hasBody && !hasImage) return null;

  return (
    <SectionCard title={section.title}>
      {hasImage && (
        <img
          src={section.image_url as string}
          alt={section.title ?? ''}
          className="mb-[10px] w-full rounded-ca-lg"
          loading="lazy"
        />
      )}
      {hasBody && <CustomProse html={section.body} />}
    </SectionCard>
  );
}

/** 요약 섹션 — 상태칩 + 제목 + 주소 + 가격. */
function renderSummary(property: Property): React.ReactElement {
  const s = summaryData(property);
  return (
    <SectionCard>
      <div className="flex flex-col gap-[6px]">
        <PropertyStatusChip status={s.status} isPreRegistration={s.isPreRegistration} isReopen={(s as any).isReopen} />
        <h1 className="text-[18px] font-extrabold leading-tight text-ink">{s.title}</h1>
        <div className="text-[12.5px] text-muted">{s.address}</div>
        <div className="text-[16px] font-extrabold text-accent-dark">{s.priceLabel}</div>
      </div>
    </SectionCard>
  );
}

/** 자동 섹션 — key 로 분기. 데이터 없으면 null. */
function renderAuto(
  section: SectionDescriptor,
  property: Property,
  variant: SectionRendererProps['variant'] = 'panel',
): React.ReactElement | null {
  switch (section.key) {
    case 'summary':
      return renderSummary(property);

    case 'price': {
      // v2 타입별 분양가 — 공고가(타입별 최고가) + 원분양가/할인가 표기 (대표 결정 2026-09-21 #5)
      const basis = getPriceBasis(property);
      // 담당자 없는 현장(is_side_inquiry)은 '정확한 금액은 1533-8124 상담' 안내를 붙이지 않는다 (2026-09-26 대표)
      const caption = buildPriceCaption(property, canContactProperty(property) ? resolveContactPhone(property).display : undefined);
      const typeRows = buildTypePriceRows(property);
      if (typeRows.length > 0) {
        return (
          <TypePriceSection
            rows={typeRows}
            priceLabel={priceBasisLabel(basis)}
            caption={caption}
            discounted={basis === 'discounted' || property.benefit_headline?.kind === 'price_cut'}
          />
        );
      }
      // 타입 행이 없으면 min/max 단일행 폴백 (기존 PriceTable)
      const rows = buildPriceRows(property);
      if (rows.length === 0) return null;
      return (
        <SectionCard title="분양가" icon={<TableIcon />}>
          <p className="mb-2 text-[12px] font-semibold text-body" data-testid="type-price-caption">{caption}</p>
          <PriceTable rows={rows} />
        </SectionCard>
      );
    }

    case 'benefit': {
      // v2i 목업 04 '혜택안내' — 혜택 1건 = 카드 1장(대표혜택 맨 앞·오렌지 테두리). 상세만 lg 3열.
      const view = buildBenefitView(property);
      if (view.items.length === 0 && !view.restricted) return null;
      return (
        <BenefitSection
          title="혜택안내"
          view={view}
          headlineId={property.benefit_headline?.benefit_id ?? null}
          layout={variant === 'detail' ? 'grid' : 'stack'}
          lockedHeadline={property.benefit_headline?.text ?? null}
        />
      );
    }

    case 'basic': {
      const rows = buildBasicRows(property);
      if (rows.length === 0) return null;
      return (
        <SectionCard title={section.title ?? '기본 정보'} icon={<InfoIcon />}>
          {rows.map((row) => (
            <DataRow key={row.label} label={row.label} value={row.value} />
          ))}
        </SectionCard>
      );
    }

    case 'floorplan': {
      const images = buildFloorPlanImages(property);
      const cells = buildPlanCells(property);
      if (images.length === 0 && cells.length === 0) return null;
      if (images.length > 0) {
        // v2: 타입 칩 탭 + 이미지, PC 좌 이미지 / 우 스펙(같은 타입의 type_prices 행)
        return (
          <FloorplanTabs
            images={images}
            priceRows={buildTypePriceRows(property)}
            pyeongOf={(name) => pyeongForType(property, name)}
            priceLabel={priceBasisLabel(getPriceBasis(property))}
          />
        );
      }
      return (
        <SectionCard title="평면도" icon={<PlanIcon />}>
          <PlanGrid cells={cells} />
        </SectionCard>
      );
    }

    case 'schedule': {
      // v2 청약일정 6단계 — 날짜 없는 단계 미렌더, 전부 없으면 청약 접수 한 줄 폴백
      const steps = buildScheduleSteps(property, todayLocalISO());
      if (steps.length === 0) {
        const schedule = formatSubscriptionDate(property);
        if (!schedule) return null;
        return (
          <SectionCard title="청약 일정" icon={<CalendarIcon />}>
            <DataRow label="청약" value={schedule} />
          </SectionCard>
        );
      }
      return <ScheduleTimeline steps={steps} />;
    }

    case 'location': {
      // v1 최소: 좌표가 있으면 지도 자리 placeholder, 없으면 null.
      // (POI 자동화는 후속 — 관리자 커스텀 섹션으로 채우는 게 기본)
      const hasCoords =
        typeof property.latitude === 'number' && typeof property.longitude === 'number';
      if (!hasCoords) return null;
      return (
        <SectionCard title={section.title ?? '위치'}>
          <div
            className="h-[160px] w-full rounded-ca-lg bg-block"
            aria-label={`${property.address} 지도`}
          />
          {property.address && (
            <p className="mt-[10px] text-[12.5px] text-muted">{property.address}</p>
          )}
        </SectionCard>
      );
    }

    default:
      return null;
  }
}

export function SectionRenderer({ section, property, variant = 'panel' }: SectionRendererProps) {
  if (section.kind === 'custom') return renderCustom(section);
  if (section.kind === 'auto') return renderAuto(section, property, variant);
  return null;
}

export default SectionRenderer;
